import { ReactNode, useEffect, useState } from "react";

/** Simple bottom sheet: backdrop tap / Escape closes it; slides up on open. */
export function BottomSheet({
  open,
  onClose,
  children,
  locked = false,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** true while an action is running: the sheet can't be dismissed */
  locked?: boolean;
}) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !locked) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, locked, onClose]);

  if (!mounted) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/55 transition-opacity duration-200"
        style={{ opacity: shown ? 1 : 0 }}
        onClick={() => !locked && onClose()}
      />
      <div
        className="relative w-full rounded-t-3xl px-4 pt-3 transition-transform duration-200 ease-out"
        style={{
          transform: shown ? "translateY(0)" : "translateY(100%)",
          background: "var(--tg-theme-secondary-bg-color, #1b1f27)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          paddingBottom: "max(16px, env(safe-area-inset-bottom))",
          maxHeight: "88%",
          overflowY: "auto",
        }}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
        {children}
      </div>
    </div>
  );
}
